import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Box, List, ListItem, ListItemText } from '@mui/material';

import { apiRequest } from '@/src/services';
import { DataTable, type TableColumn, Loading, ErrorMessage } from '@/src/ui';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type { StandardsReportSummaryDTO } from '../models/standardsReportDTOs';

interface StandardsTableProps {
    data: StandardsReportSummaryDTO[];
    isLoading: boolean;
    error: string;
    emptyMessage?: string;
    examId?: number;
    useSeed?: boolean;
}

interface DisplayRow {
    standardId: number;
    standardCode: string;
    skillDescription: string;
    displayCorrect: number;
    displayTotal: number;
    displayPercent: number;
    subs: { id: number; code: string }[];
    studentsAssigned: number;
    studentsCompleted: number;
}

const StandardsReportTable: React.FC<StandardsTableProps> = ({
    data,
    isLoading,
    error,
    emptyMessage,
    examId,
    useSeed,
}) => {
    const [open, setOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [qLoading, setQLoading] = useState(false);
    const [qError, setQError] = useState<string | null>(null);
    const [questions, setQuestions] = useState<{ questionId: number; title: string }[]>([]);

    const openQuestions = useCallback(
        async (standardId: number, standardCode: string) => {
            setOpen(true);
            setModalTitle(`${standardCode} - Questions`);
            setQLoading(true);
            setQError(null);
            setQuestions([]);

            try {
                const path = useSeed
                    ? `reports/standardsReport/questions/seed?standardId=${standardId}`
                    : `reports/standardsReport/questions?standardId=${standardId}`;

                const { data } = await apiRequest<{ questionId: number; title: string }[]>(
                    'GET',
                    path
                );
                setQuestions(Array.isArray(data) ? data : []);
            } catch (e: any) {
                setQError(e?.message || 'Failed to load questions.');
            } finally {
                setQLoading(false);
            }
        },
        [useSeed]
    );

    const rows: DisplayRow[] = useMemo(() => {
        const out: DisplayRow[] = [];
        for (const row of data) {
            out.push({
                standardId: row.standardId,
                standardCode: row.standardCode,
                skillDescription: row.skillDescription,

                displayCorrect: row.groupedCorrect ?? row.questionsCorrect,
                displayTotal: row.groupedTotal ?? row.questionsTotal,
                displayPercent: row.groupedPercentCorrect ?? row.percentCorrect,

                subs: Array.isArray(row.substandards)
                    ? row.substandards.map((s) => ({ id: s.standardId, code: s.standardCode }))
                    : [],

                studentsAssigned: row.studentsAssigned,
                studentsCompleted: row.studentsCompleted,
            });
        }
        return out;
    }, [data]);

    const columns: TableColumn[] = useMemo(
        () => [
            {
                dataKey: 'standardCode',
                header: 'Standard',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => (
                    <span
                        style={{ whiteSpace: 'nowrap', display: 'inline-block', lineHeight: 1.4 }}
                    >
                        {/* Parent code as link */}
                        <Link
                            to="#"
                            onClick={(e) => {
                                e.preventDefault();
                                if (useSeed || examId) {
                                    openQuestions(r.standardId, r.standardCode);
                                }
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                            style={{
                                cursor: useSeed || examId ? 'pointer' : 'not-allowed',
                            }}
                            title={
                                useSeed || examId ? 'View questions' : 'Select an assessment first'
                            }
                        >
                            {r.standardCode}
                        </Link>

                        {/* Substandard codes as links */}
                        {r.subs.length > 0 && (
                            <span style={{ opacity: 0.9 }}>
                                {', '}
                                {r.subs.map((s, i) => (
                                    <React.Fragment key={s.id}>
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (useSeed || examId) {
                                                    openQuestions(s.id, s.code);
                                                }
                                            }}
                                            style={{
                                                cursor:
                                                    useSeed || examId ? 'pointer' : 'not-allowed',
                                            }}
                                            title={
                                                useSeed || examId
                                                    ? 'View questions'
                                                    : 'Select an assessment first'
                                            }
                                        >
                                            {s.code}
                                        </Link>
                                        {i < r.subs.length - 1 ? ', ' : ''}
                                    </React.Fragment>
                                ))}
                            </span>
                        )}
                    </span>
                ),
            },
            {
                dataKey: 'skillDescription',
                header: 'Skill / Description',
                allowSort: true,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'displayCorrect',
                header: 'Correct',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => `${r.displayCorrect ?? 0}`,
            },
            {
                dataKey: 'displayTotal',
                header: 'Total',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => `${r.displayTotal ?? 0}`,
            },
            {
                dataKey: 'displayPercent',
                header: 'Percent',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => `${r.displayPercent ?? 0}%`,
            },
            {
                dataKey: 'studentsCompleted',
                header: 'Students Completed',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => `${r.studentsCompleted ?? 0}`,
            },
            {
                dataKey: 'studentsAssigned',
                header: 'Students Assigned',
                allowSort: true,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => `${r.studentsAssigned ?? 0}`,
            },
        ],
        [useSeed, examId, openQuestions]
    );

    if (isLoading) return <Loading message="Loading standards..." />;

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mb: 1 }}>
                <ErrorMessage error={error} />
            </Box>
        );
    }

    return (
        <>
            <div className="pl-4">
                <h2
                    className="inline-block whitespace-nowrap text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide"
                    style={{
                        fontFamily: 'Verdana, Geneva, sans-serif',
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                    }}
                >
                    Class Skill Summary
                </h2>
            </div>
            <DataTable
                data={rows}
                columns={columns}
                tableName="StandardsReportTable"
                emptyMessage={emptyMessage}
                rowsPerPageInitial={20}
                enableExportCSV
                alternatingRowColor
                defaultSortColumn="standardCode"
                defaultSortDirection="asc"
            />

            <Modal open={open} onClose={() => setOpen(false)}>
                <Box
                    sx={{
                        p: 3,
                        bgcolor: 'background.paper',
                        m: 'auto',
                        mt: 8,
                        width: 700,
                        maxWidth: '95%',
                        maxHeight: '80vh',
                        borderRadius: 2,
                        boxShadow: 24,
                        overflowY: 'auto',
                    }}
                >
                    <h2 style={{ marginTop: 0 }}>{modalTitle}</h2>

                    <Loading visible={qLoading} size="md" />
                    <ErrorMessage error={qError} />

                    {!qLoading && !qError && (
                        <List dense>
                            {questions.length === 0 ? (
                                <ListItem>
                                    <ListItemText primary="No questions found for this standard in the selected exam." />
                                </ListItem>
                            ) : (
                                questions.map((q) => (
                                    <ListItem key={q.questionId}>
                                        <ListItemText primary={q.title || 'Untitled question'} />
                                    </ListItem>
                                ))
                            )}
                        </List>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default StandardsReportTable;
